import { EInvoiceAddressSchemeType, EntityType } from '@components'

import { TFunction } from 'i18next'
import { z } from 'zod'

import { CustomerType } from '@features/contacts/enums/customerType'
import { getIsAddressExemptionAvailable } from '@features/contacts/utils/getIsAddressExemptionAvailable'
import { getVatNumberFieldInvalidFormatErrorMessage } from '@features/invoices/utils/getVatNumberFieldInvalidFormatErrorMessage'

import { getVatNoRegex } from '../../../../../../../utils'
import { MissingCustomerDetailsFormSchema } from '../types/missingCustomerDetailsFormSchema'
import { getCustomerVatNoFieldConfig } from './getCustomerVatNoFieldConfig'

export const getFrenchCustomerFormValidationSchema = (t: TFunction): MissingCustomerDetailsFormSchema => {
  return z
    .object({
      address: z.object({
        city: z.string().trim().optional(),
        country: z.string(),
        pobox: z.string().trim().optional(),
        streetAddress1: z.string().trim().optional(),
        streetAddress2: z.string().optional(),
      }),
      companyName: z.string().optional(),
      customerType: z.nativeEnum(CustomerType),
      eInvoiceAddress: z
        .object({
          address: z
            .string()
            .trim()
            .transform((address) => address.replace(/\s/g, ''))
            .optional(),
          scheme: z.nativeEnum(EInvoiceAddressSchemeType).optional(),
        })
        .optional(),
      entityType: z.nativeEnum(EntityType).optional(),
      firstName: z.string().optional(),
      id: z.string(),
      isAddressExempt: z.boolean().optional(),
      isVatExempted: z.boolean().optional(),
      lastName: z.string().optional(),
      vatNumber: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const { address, customerType, companyName, isAddressExempt, isVatExempted, vatNumber, firstName, lastName } =
        data
      const country = address.country
      const isAddressExemptionAvailable = getIsAddressExemptionAvailable({
        customerCountry: country,
        customerType,
        isFrenchComplianceMode: true,
      })
      const isAddressRequired = !(isAddressExemptionAvailable && isAddressExempt)

      if (isAddressRequired) {
        if (!address.streetAddress1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'external_invoices.missing_customer_compliance_details.modal.form.field.address.street_address_1.error.required',
            ),
            path: ['address', 'streetAddress1'],
          })
        }
        if (!address.pobox) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'external_invoices.missing_customer_compliance_details.modal.form.field.address.pobox.error.required',
            ),
            path: ['address', 'pobox'],
          })
        }
        if (!address.city) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'external_invoices.missing_customer_compliance_details.modal.form.field.address.city.error.required',
            ),
            path: ['address', 'city'],
          })
        }
      }

      if (customerType === CustomerType.Company) {
        if (!companyName?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'external_invoices.missing_customer_compliance_details.modal.form.field.customer_company_name.error.required',
            ),
            path: ['companyName'],
          })
        }
        if (!isVatExempted && !vatNumber?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: getCustomerVatNoFieldConfig(country, t).errorRequired,
            path: ['vatNumber'],
          })
        } else if (!isVatExempted && vatNumber && !getVatNoRegex(country).test(vatNumber)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: getVatNumberFieldInvalidFormatErrorMessage(country, t),
            path: ['vatNumber'],
          })
        }
      } else if (customerType === CustomerType.Individual) {
        if (!firstName?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'external_invoices.missing_customer_compliance_details.modal.form.field.first_name.error.required',
            ),
            path: ['firstName'],
          })
        }
        if (!lastName?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'external_invoices.missing_customer_compliance_details.modal.form.field.last_name.error.required',
            ),
            path: ['lastName'],
          })
        }
      }
    })
}
